import { KeyboardEvent, useEffect, useState } from "react";
import { styled } from "styled-components";
import { createAuctionPlaceholderImage } from "../../lib/auctionPresentation";

const Card = styled.div<{ $interactive: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 20px;
  background: ${({ theme }) => theme.colors.primary};
  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.25);
  border-radius: 12px;
  flex: 1;
  min-width: 260px;
  cursor: ${({ $interactive }) => ($interactive ? "pointer" : "default")};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.white};
    outline-offset: 3px;
  }

  &:hover {
    transform: ${({ $interactive }) => ($interactive ? "translateY(-2px)" : "none")};
  }

  @media (min-width: 768px) {
    max-width: calc(50% - 12.5px);
  }

  @media (min-width: 900px) {
    max-width: calc(33.333% - 17px);
  }

  @media (min-width: 1200px) {
    max-width: calc(25% - 18.75px); /* 4 columns for large screens */
  }

  @media (max-width: 767px) {
    padding: 18px;
    gap: 16px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  flex: 1;
`;

const ProductLabel = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.white};
`;

const ProductName = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 28px;
  color: ${({ theme }) => theme.colors.blue500};
`;

const Avatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: ${({ theme }) => theme.gradients.darkBlue};
  box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProductImage = styled.div`
  width: 100%;
  height: 160px;
  border-radius: 8px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 767px) {
    height: 140px;
  }
`;

const PriceInfo = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  width: 100%;
`;

const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  flex: 1;
  min-width: 130px;
`;

const PriceLabel = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.white};
`;

const PriceValue = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 28px;
  color: ${({ theme }) => theme.colors.blue500};
`;

const SavingsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const SavingsLabel = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.white};
  text-align: right;
`;

const SavingsValue = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.blue500};
`;

interface ProductCardProps {
  productName: string;
  productLabel: string;
  avatarUrl?: string;
  imageUrl: string;
  soldPrice: string;
  savings: string;
  onClick?: () => void;
  priceCaption?: string;
  savingsCaption?: string;
}

const ProductCard = ({
  productName,
  productLabel,
  avatarUrl,
  imageUrl,
  soldPrice,
  savings,
  onClick,
  priceCaption = "Prodáno za cenu",
  savingsCaption = "Kupující ušetřil",
}: ProductCardProps) => {
  const fallbackImage = createAuctionPlaceholderImage(productName, productLabel);
  const [cardImageSrc, setCardImageSrc] = useState(imageUrl || fallbackImage);

  useEffect(() => {
    setCardImageSrc(imageUrl || fallbackImage);
  }, [fallbackImage, imageUrl]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Card
      $interactive={!!onClick}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <CardHeader>
        <ProductInfo>
          <ProductLabel>{productLabel}</ProductLabel>
          <ProductName>{productName}</ProductName>
        </ProductInfo>
        {avatarUrl && (
          <Avatar>
            <img src={avatarUrl} alt="Výherce aukce" />
          </Avatar>
        )}
      </CardHeader>

      <ProductImage>
        <img
          src={cardImageSrc}
          alt={productName}
          onError={() => {
            if (cardImageSrc !== fallbackImage) {
              setCardImageSrc(fallbackImage);
            }
          }}
        />
      </ProductImage>

      <PriceInfo>
        <PriceBlock>
          <PriceLabel>{priceCaption}</PriceLabel>
          <PriceValue>{soldPrice}</PriceValue>
        </PriceBlock>
        <SavingsBlock>
          <SavingsLabel>{savingsCaption}</SavingsLabel>
          <SavingsValue>{savings}</SavingsValue>
        </SavingsBlock>
      </PriceInfo>
    </Card>
  );
};

export default ProductCard;
